import { gql } from '@apollo/client';

/**
 * Agent command plane — the GraphQL documents, row shapes and per-op arg specs
 * shared by the fleet page, the command dialog and the commands list/detail
 * pages. One place so the four surfaces can't drift.
 *
 * The flow is strictly PREVIEW → DRAFT → APPROVE. There is no enqueue: a draft
 * is the only thing that can be approved, and approve must echo back the
 * DRAFT'S OWN `planDigest` (a draft freezes server-generated values such as
 * `restartedAt`, so a fresh preview of the same inputs legitimately digests
 * differently — the preview is for the eyes, the draft's digest is the contract).
 */

// Field selection for AgentCommandType. NB: there is deliberately no `output`
// field — it can carry pod logs and has its own gated query.
const CMD_FIELDS = `
  id
  commandId
  clusterName
  agentId
  target
  op
  args
  mutating
  planSummary
  planDiff
  planDigest
  status
  requestedBy
  approvedBy
  approvedAt
  claimedByAgentId
  claimedAt
  leaseExpiresAt
  attempt
  cancelRequested
  expiresAt
  startedAt
  finishedAt
  exitCode
  error
  result
  verifiedAt
  createdAt
`;

export const AGENT_COMMANDS = gql`
  query AgentCommands($clusterName: String, $agentId: String, $status: String, $limit: Int) {
    agentCommands(clusterName: $clusterName, agentId: $agentId, status: $status, limit: $limit) {
      ${CMD_FIELDS}
    }
    clusterAgentSettings {
      clusterName
      desiredVersion
      commandsEnabled
      isProduction
    }
  }
`;

export const AGENT_COMMAND = gql`
  query AgentCommand($id: String!) {
    agentCommand(id: $id) {
      ${CMD_FIELDS}
    }
    agentCommandEvents(id: $id) {
      id
      kind
      actor
      note
      meta
      createdAt
    }
    clusterAgentSettings {
      clusterName
      desiredVersion
      commandsEnabled
      isProduction
    }
  }
`;

/** Root|maintainer only, and pod logs may be inside — never auto-fetch this. */
export const AGENT_COMMAND_OUTPUT = gql`
  query AgentCommandOutput($id: String!) {
    agentCommandOutput(id: $id)
  }
`;

export const AGENT_COMMAND_PLAN = gql`
  query AgentCommandPlan($clusterName: String!, $op: String!, $args: String) {
    agentCommandPlan(clusterName: $clusterName, op: $op, args: $args) {
      op
      clusterName
      agentId
      target
      mutating
      production
      summary
      diff
      warnings
      args
      digest
    }
  }
`;

export const DRAFT_AGENT_COMMAND = gql`
  mutation DraftAgentCommand($clusterName: String!, $op: String!, $args: String) {
    draftAgentCommand(clusterName: $clusterName, op: $op, args: $args) {
      ${CMD_FIELDS}
    }
  }
`;

export const APPROVE_AGENT_COMMAND = gql`
  mutation ApproveAgentCommand($id: String!, $planDigest: String!, $note: String) {
    approveAgentCommand(id: $id, planDigest: $planDigest, note: $note) {
      ${CMD_FIELDS}
    }
  }
`;

export const REJECT_AGENT_COMMAND = gql`
  mutation RejectAgentCommand($id: String!, $note: String) {
    rejectAgentCommand(id: $id, note: $note) {
      id
      status
    }
  }
`;

export const CANCEL_AGENT_COMMAND = gql`
  mutation CancelAgentCommand($id: String!, $note: String) {
    cancelAgentCommand(id: $id, note: $note) {
      id
      status
      cancelRequested
    }
  }
`;

// ── row shapes ───────────────────────────────────────────────────────────────

export interface AgentCommand {
  id: string;
  commandId: string;
  clusterName: string;
  agentId?: string | null;
  target: string;
  op: string;
  /** JSON */
  args: string;
  mutating: boolean;
  planSummary?: string | null;
  planDiff?: string | null;
  planDigest?: string | null;
  status: string;
  requestedBy: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
  claimedByAgentId?: string | null;
  claimedAt?: string | null;
  leaseExpiresAt?: string | null;
  attempt: number;
  cancelRequested: boolean;
  expiresAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  exitCode?: number | null;
  error?: string | null;
  /** JSON */
  result: string;
  verifiedAt?: string | null;
  createdAt: string;
}

export interface AgentCommandEvent {
  id: string;
  kind: string;
  actor: string;
  note?: string | null;
  /** JSON */
  meta: string;
  createdAt: string;
}

export interface AgentCommandPlan {
  op: string;
  clusterName: string;
  agentId?: string | null;
  target: string;
  mutating: boolean;
  production: boolean;
  summary: string;
  diff: string;
  warnings: string[];
  /** JSON — the server-normalized args (may add defaults such as restartedAt). */
  args: string;
  digest: string;
}

export interface ClusterAgentSettings {
  clusterName: string;
  desiredVersion?: string | null;
  commandsEnabled: boolean;
  isProduction: boolean;
  allowedNamespaces?: string[];
  allowedOps?: string[];
  updatedBy?: string | null;
  updatedAt?: string;
}

export interface AgentVersionRollup {
  clusterName: string;
  desiredVersion?: string | null;
  total: number;
  onDesired: number;
  leaderAgentId?: string | null;
  commandsEnabled: boolean;
  isProduction: boolean;
}

export interface AgentRelease {
  version: string;
  image: string;
  notes?: string | null;
  isDefault: boolean;
  publishedBy: string;
  publishedAt: string;
}

// ── ops ──────────────────────────────────────────────────────────────────────

/** Closed allowlist — mirrors AGENT_COMMAND_OPS in inventory-core. */
export const AGENT_OPS = [
  'pod.list',
  'workload.status',
  'pod.logs',
  'daemonset.restart',
  'deployment.restart',
  'deployment.scale',
  'agent.self_upgrade',
] as const;

export const READ_ONLY_OPS: string[] = ['pod.list', 'workload.status', 'pod.logs'];

export const isMutatingOp = (op: string) => !READ_ONLY_OPS.includes(op);

/** One choice of a `select` field: `value` goes on the wire, `label` is what the operator reads. */
export interface OpFieldOption {
  value: string;
  label: string;
}

export interface OpField {
  key: string;
  label: string;
  required?: boolean;
  kind?: 'text' | 'number' | 'select';
  options?: OpFieldOption[];
  placeholder?: string;
  /**
   * Prefilled as a real, editable value. For a field the server would default
   * anyway when left blank, so the operator reads the value that will run (and
   * the frozen plan carries it) instead of a grey hint that is not sent.
   */
  defaultValue?: string;
  help?: string;
}

/**
 * The namespace the server applies when the field is left blank. Mirrors
 * `AGENT_DEFAULT_NAMESPACE` in inventory-core (a server package the web cannot import).
 */
export const DEFAULT_NAMESPACE = 'platform';

const NAMESPACE: OpField = {
  key: 'namespace',
  label: 'Namespace',
  placeholder: DEFAULT_NAMESPACE,
  defaultValue: DEFAULT_NAMESPACE,
};

/**
 * Arg form per op. Keys and values are the WIRE shape: the in-cluster agent
 * parses the stored args strictly and refuses a key or an enum value it does not
 * know, so the form sends exactly what the agent accepts (`name` for a pod,
 * lowercase workload kinds). The server's `validateCommandArgs` in
 * inventory-core emits the same shape and re-validates. Kept as plain
 * descriptors (not a schema lib) because it is seven small shapes.
 *
 * `agent.self_upgrade` takes the image only. The workload it patches is fixed
 * by the server, so there is nothing else for the operator to choose.
 */
export const OP_FIELDS: Record<string, OpField[]> = {
  'pod.list': [
    NAMESPACE,
    { key: 'labelSelector', label: 'Label selector', placeholder: 'app=optisigns-agent' },
  ],
  'workload.status': [
    {
      key: 'kind',
      label: 'Kind',
      required: true,
      kind: 'select',
      options: [
        { value: 'daemonset', label: 'DaemonSet' },
        { value: 'deployment', label: 'Deployment' },
      ],
    },
    { key: 'name', label: 'Name', required: true, placeholder: 'optisigns-agent' },
    NAMESPACE,
  ],
  'pod.logs': [
    { key: 'name', label: 'Pod name', required: true, placeholder: 'optisigns-agent-abcde' },
    { key: 'container', label: 'Container', placeholder: '(default)' },
    NAMESPACE,
    { key: 'tailLines', label: 'Tail lines', kind: 'number', placeholder: '200', help: '1–1000' },
  ],
  'daemonset.restart': [
    { key: 'name', label: 'DaemonSet', required: true, placeholder: 'optisigns-agent' },
    NAMESPACE,
  ],
  'deployment.restart': [{ key: 'name', label: 'Deployment', required: true }, NAMESPACE],
  'deployment.scale': [
    { key: 'name', label: 'Deployment', required: true },
    NAMESPACE,
    { key: 'replicas', label: 'Replicas', required: true, kind: 'number', help: '0–50' },
  ],
  'agent.self_upgrade': [
    {
      key: 'image',
      label: 'Image',
      required: true,
      placeholder: 'registry/optisigns-agent:1.4.0',
      // The agent's rule (apps/agent/src/commands/validate.ts, imageRef); the server
      // refuses anything else when the plan is built.
      help: 'semver tag (1.4.0, v1.4.0-sbx) or @sha256 digest; a tag such as sbx-<sha> or latest is refused',
    },
  ],
};

/**
 * Starting values for an op's form: each field's `defaultValue`, overridden by
 * the caller's prefill. A prefill key the op has no field for is dropped, so
 * nothing the operator cannot see is carried into the args.
 */
export function defaultArgs(op: string, prefill: Record<string, string> = {}): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of OP_FIELDS[op] ?? []) {
    const v = prefill[f.key] ?? f.defaultValue;
    if (v !== undefined) out[f.key] = v;
  }
  return out;
}

/**
 * What an op reaches beyond what its arg form shows. The dialog keeps this on
 * screen until the command is approved, not only while the form is open.
 *
 * `daemonset.restart` is the one that needs saying: the fleet page offers it on
 * a node row, and it restarts the DaemonSet's pod on every node.
 */
export function opNote(op: string, clusterName: string, args: Record<string, string>): string | null {
  if (op === 'daemonset.restart') {
    const name = args.name?.trim();
    return (
      `This restarts the whole ${name ? `${name} ` : ''}DaemonSet for ${clusterName}: its pod on every node ` +
      'is replaced in a rolling restart. It is not a restart of one node, whichever row it was started from.'
    );
  }
  return null;
}

export const OP_LABEL: Record<string, string> = {
  'pod.list': 'List pods',
  'workload.status': 'Workload status',
  'pod.logs': 'Pod logs',
  'daemonset.restart': 'Restart DaemonSet',
  'deployment.restart': 'Restart Deployment',
  'deployment.scale': 'Scale Deployment',
  'agent.self_upgrade': 'Upgrade agent',
};

/**
 * Command status → `.badge` modifier. Terminal-good is green, human/expiry
 * outcomes are amber, failure is red, in-flight keeps the neutral badge.
 */
export const CMD_STATUS_CLASS: Record<string, string> = {
  draft: 'quiet',
  queued: '',
  claimed: '',
  running: '',
  applying: '',
  verifying: '',
  succeeded: 'confirmed',
  failed: 'failed',
  expired: 'inferred',
  cancelled: 'quiet',
  rejected: 'inferred',
};

/** Statuses that are still on their way through the pipeline. */
export const IN_FLIGHT = ['queued', 'claimed', 'running', 'applying', 'verifying'];

// ── small formatters (shared with the fleet page) ────────────────────────────

/** Compact relative time — "1m ago" / "2h ago" / "92d ago". */
export function timeAgo(iso?: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function prettyJson(s?: string | null): string {
  if (!s) return '{}';
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/**
 * Strip the transport prefix Apollo/Nest add so the server's own words show
 * verbatim ("the plan changed since it was rendered — …").
 */
export function serverMessage(err: unknown): string {
  const msg = (err as { message?: string } | undefined)?.message ?? String(err ?? '');
  return msg.replace(/^ApolloError:\s*/, '').trim();
}

/** The digest-mismatch refusal deserves a specific nudge, not a generic retry. */
export const isPlanConflict = (msg: string) => /plan changed|no longer a draft|is \w+, only a draft/i.test(msg);
